import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../../providers/TenantProvider';

export interface AuditLog {
  id: string;
  tenantId: string;
  userId: string;
  userEmail: string;
  userName?: string;
  action: string;
  resourceType: string;
  resourceId?: string;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  phiAccessed: boolean;
  timestamp: string;
}

export interface AuditLogFilters {
  startDate?: string;
  endDate?: string;
  userId?: string;
  action?: string;
  resourceType?: string;
  phiOnly?: boolean;
  search?: string;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export function useAuditLogs() {
  const { getAccessTokenSilently } = useAuth0();
  const { currentTenant } = useTenant();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableActions, setAvailableActions] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<{ id: string; email: string }[]>([]);

  const fetchLogs = useCallback(
    async (filters: AuditLogFilters = {}, page = 1, pageSize = 25) => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());

        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.userId) params.append('userId', filters.userId);
        if (filters.action) params.append('action', filters.action);
        if (filters.resourceType) params.append('resourceType', filters.resourceType);
        if (filters.phiOnly) params.append('phiOnly', 'true');
        if (filters.search) params.append('search', filters.search);

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/audit/${currentTenant.id}/logs?${params}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'x-tenant-id': currentTenant.id,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch audit logs');
        }

        const data = await response.json();
        setLogs(data.logs || []);
        setPagination({
          page: data.page || 1,
          pageSize: data.pageSize || 25,
          total: data.total || 0,
          totalPages: data.totalPages || 0,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  const fetchFilterOptions = useCallback(async () => {
    if (!currentTenant) return;

    try {
      const token = await getAccessTokenSilently();
      // Note: /filters endpoint needs to be implemented server-side
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/audit/${currentTenant.id}/filters`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch filter options');
      }

      const data = await response.json();
      setAvailableActions(data.actions || []);
      setAvailableUsers(data.users || []);
    } catch {
      // Silently fail - not critical
    }
  }, [currentTenant, getAccessTokenSilently]);

  const exportLogs = useCallback(
    async (filters: AuditLogFilters = {}, format: 'csv' | 'json' = 'csv') => {
      if (!currentTenant) return null;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams();
        params.append('format', format);

        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.userId) params.append('userId', filters.userId);
        if (filters.action) params.append('action', filters.action);
        if (filters.resourceType) params.append('resourceType', filters.resourceType);
        if (filters.phiOnly) params.append('phiOnly', 'true');
        if (filters.search) params.append('search', filters.search);

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/audit/${currentTenant.id}/logs/export?${params}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'x-tenant-id': currentTenant.id,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to export audit logs');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  return {
    logs,
    pagination,
    isLoading,
    error,
    availableActions,
    availableUsers,
    fetchLogs,
    fetchFilterOptions,
    exportLogs,
  };
}
